// Personalakte (Etappe 6) — reine Regeln und Datenzugriffe.
//
// Bewusst NICHT erfasst: Bankverbindung, Steuer-ID, Sozialversicherungsnummer.
// Die gehören in die Lohnbuchhaltung; hier würden sie nur Angriffsfläche und
// DSGVO-Pflichten schaffen, ohne im Alltag gebraucht zu werden.
import { supabase } from '../supabase/client';

export interface Personalakte {
  mitarbeiter_id: string;
  personalnummer: string | null;
  position: string | null;
  abteilung: string | null;
  eintritt: string | null;
  austritt: string | null;
  befristet_bis: string | null;
  geburtsdatum: string | null;
  telefon_privat: string | null;
  adresse: string | null;
  notfall_name: string | null;
  notfall_telefon: string | null;
  bemerkung: string | null;
}

export type DokumentArt = 'vertrag' | 'zeugnis' | 'bescheinigung' | 'schulung' | 'unterweisung' | 'fuehrerschein' | 'sonstiges';
export type NachweisArt = 'fuehrerschein' | 'unterweisung' | 'schulung' | 'untersuchung';

export interface PersonalDokument {
  id: string;
  mitarbeiter_id: string;
  art: DokumentArt;
  titel: string;
  datei_path: string | null;
  datei_name: string | null;
  gueltig_bis: string | null;
  created_at: string;
}

export interface Nachweis {
  id: string;
  mitarbeiter_id: string;
  art: NachweisArt;
  titel: string | null;
  durchgefuehrt_am: string;
  naechste_faellig: string | null;
  bemerkung: string | null;
  created_at: string;
}

export const DOKUMENT_ART: Record<DokumentArt, string> = {
  vertrag: 'Arbeitsvertrag',
  zeugnis: 'Zeugnis',
  bescheinigung: 'Bescheinigung',
  schulung: 'Schulung',
  unterweisung: 'Unterweisung',
  fuehrerschein: 'Führerschein',
  sonstiges: 'Sonstiges',
};

export const NACHWEIS_ART: Record<NachweisArt, { label: string; intervallMonate: number }> = {
  // §21 DGUV V70: Führerscheinkontrolle üblicherweise halbjährlich
  fuehrerschein: { label: 'Führerscheinkontrolle', intervallMonate: 6 },
  // §12 ArbSchG: Unterweisung mindestens jährlich
  unterweisung: { label: 'Arbeitsschutzunterweisung', intervallMonate: 12 },
  schulung: { label: 'Schulung', intervallMonate: 24 },
  untersuchung: { label: 'Vorsorgeuntersuchung', intervallMonate: 36 },
};

/** Vorschlag für die nächste Fälligkeit aus Art und Durchführungsdatum. */
export function naechsteFaelligkeit(art: NachweisArt, durchgefuehrtAm: string): string {
  const d = new Date(`${durchgefuehrtAm}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + (NACHWEIS_ART[art]?.intervallMonate ?? 12));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export type Ampel = 'ok' | 'bald' | 'faellig' | 'unbekannt';

/** Wie dringend ist ein Datum? „bald" = innerhalb von 30 Tagen. */
export function ampel(faellig: string | null | undefined, heute: Date = new Date()): Ampel {
  if (!faellig) return 'unbekannt';
  const d = new Date(`${faellig}T12:00:00`);
  if (Number.isNaN(d.getTime())) return 'unbekannt';
  const tage = Math.floor((d.getTime() - heute.getTime()) / 86400000);
  if (tage < 0) return 'faellig';
  if (tage <= 30) return 'bald';
  return 'ok';
}

/** Der jeweils jüngste Nachweis je Art — nur der bestimmt die nächste Fälligkeit. */
export function aktuelleNachweise(nachweise: Nachweis[]): Nachweis[] {
  const map = new Map<string, Nachweis>();
  for (const n of nachweise) {
    const vorhanden = map.get(n.art);
    if (!vorhanden || n.durchgefuehrt_am > vorhanden.durchgefuehrt_am) map.set(n.art, n);
  }
  return [...map.values()].sort((a, b) => (a.naechste_faellig || '9999') < (b.naechste_faellig || '9999') ? -1 : 1);
}

/** Betriebszugehörigkeit in vollen Jahren; ohne Eintrittsdatum null. */
export function dienstjahre(eintritt: string | null | undefined, heute: Date = new Date()): number | null {
  if (!eintritt) return null;
  const d = new Date(`${eintritt}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  let jahre = heute.getFullYear() - d.getFullYear();
  const vorGeburtstag = heute.getMonth() < d.getMonth()
    || (heute.getMonth() === d.getMonth() && heute.getDate() < d.getDate());
  if (vorGeburtstag) jahre -= 1;
  return Math.max(0, jahre);
}

// ── Datenzugriffe ────────────────────────────────────────────────────────────

export async function ladeAkte(mitarbeiterId: string): Promise<Personalakte | null> {
  const { data, error } = await supabase.from('personalakte').select('*').eq('mitarbeiter_id', mitarbeiterId).maybeSingle();
  if (error) { console.error('Personalakte laden fehlgeschlagen', error.message); return null; }
  return (data as Personalakte) || null;
}

export async function akteSpeichern(akte: Partial<Personalakte> & { mitarbeiter_id: string }): Promise<string | null> {
  const { error } = await supabase.from('personalakte').upsert(akte, { onConflict: 'mitarbeiter_id' });
  return error ? error.message : null;
}

export async function ladeDokumente(mitarbeiterId: string): Promise<PersonalDokument[]> {
  const { data, error } = await supabase
    .from('personal_dokumente').select('*').eq('mitarbeiter_id', mitarbeiterId).order('created_at', { ascending: false });
  if (error) { console.error('Dokumente laden fehlgeschlagen', error.message); return []; }
  return (data || []) as PersonalDokument[];
}

export async function dokumentHochladen(args: {
  mitarbeiterId: string; art: DokumentArt; titel: string; datei: File | null;
  gueltigBis?: string | null; vonId: string;
}): Promise<string | null> {
  let path: string | null = null;
  let name: string | null = null;
  if (args.datei) {
    if (args.datei.size > 20 * 1024 * 1024) return 'Die Datei ist größer als 20 MB.';
    const endung = (args.datei.name.split('.').pop() || 'pdf').toLowerCase().slice(0, 5);
    path = `${args.mitarbeiterId}/${crypto.randomUUID()}.${endung}`;
    name = args.datei.name;
    const { error } = await supabase.storage.from('personalakte').upload(path, args.datei, {
      contentType: args.datei.type || 'application/pdf',
    });
    if (error) return `Hochladen fehlgeschlagen: ${error.message}`;
  }
  const { error } = await supabase.from('personal_dokumente').insert({
    mitarbeiter_id: args.mitarbeiterId, art: args.art, titel: args.titel.trim(),
    datei_path: path, datei_name: name, gueltig_bis: args.gueltigBis || null,
    hochgeladen_von: args.vonId,
  });
  return error ? error.message : null;
}

export async function dokumentLoeschen(d: PersonalDokument): Promise<boolean> {
  if (d.datei_path) await supabase.storage.from('personalakte').remove([d.datei_path]);
  const { error } = await supabase.from('personal_dokumente').delete().eq('id', d.id);
  return !error;
}

export async function dokumentUrl(path: string, dateiname?: string | null): Promise<string | null> {
  const { data } = await supabase.storage.from('personalakte')
    .createSignedUrl(path, 3600, dateiname ? { download: dateiname } : undefined);
  return data?.signedUrl || null;
}

export async function ladeNachweise(mitarbeiterId: string): Promise<Nachweis[]> {
  const { data, error } = await supabase
    .from('personal_nachweise').select('*').eq('mitarbeiter_id', mitarbeiterId)
    .order('durchgefuehrt_am', { ascending: false });
  if (error) { console.error('Nachweise laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Nachweis[];
}

export async function nachweisErfassen(args: {
  mitarbeiterId: string; art: NachweisArt; titel?: string | null; durchgefuehrtAm: string;
  naechsteFaellig?: string | null; bemerkung?: string | null; vonId: string;
}): Promise<string | null> {
  const { error } = await supabase.from('personal_nachweise').insert({
    mitarbeiter_id: args.mitarbeiterId, art: args.art, titel: args.titel || null,
    durchgefuehrt_am: args.durchgefuehrtAm,
    naechste_faellig: args.naechsteFaellig || naechsteFaelligkeit(args.art, args.durchgefuehrtAm),
    bemerkung: args.bemerkung || null, durchgefuehrt_von: args.vonId,
  });
  return error ? error.message : null;
}

export async function nachweisLoeschen(id: string): Promise<boolean> {
  const { error } = await supabase.from('personal_nachweise').delete().eq('id', id);
  return !error;
}

/** Alle Nachweise aller Mitarbeiter — für die Übersicht „was läuft ab?". */
export async function ladeAlleNachweise(): Promise<Nachweis[]> {
  const { data, error } = await supabase
    .from('personal_nachweise').select('*').order('durchgefuehrt_am', { ascending: false });
  if (error) { console.error('Nachweise laden fehlgeschlagen', error.message); return []; }
  return (data || []) as Nachweis[];
}
